














































































































import moment from 'moment';
import { loadStripe } from '@stripe/stripe-js';
import { Component, Prop, Vue } from 'vue-property-decorator';
import { snackModule } from '@/store';
import {
  BillingSession,
  Product,
  Subscription,
  CheckoutSession,
  ApiError,
  User,
} from '@/api';

@Component({
  components: {},
})
export default class extends Vue {
  @Prop({ required: false }) readonly user: User;

  products: Product[] = [];

  stripe: any;

  errorMessage: string | null = null;

  subscriptions: Subscription[] = [];

  get indoorProduct() {
    if (!this.products.length) {
      return;
    }

    return this.products.find(
      product => product.metadata.codename === 'unlimited_indoor_parking',
    );
  }

  get indoorPrice() {
    if (!this.indoorProduct || !this.indoorProduct.prices.length) {
      return;
    }
    return this.indoorProduct.prices[this.indoorProduct.prices.length - 1];
  }

  get outdoorProduct() {
    if (!this.products.length) {
      return;
    }

    return this.products.find(
      product => product.metadata.codename === 'unlimited_outdoor_parking',
    );
  }

  get outdoorPrice() {
    if (!this.outdoorProduct || !this.outdoorProduct.prices.length) {
      return;
    }

    return this.outdoorProduct.prices[this.outdoorProduct.prices.length - 1];
  }

  get canManageIndoor() {
    if (!this.subscriptions.length) {
      return false;
    }

    const subs = this.subscriptions.some(
      (sub: Subscription) =>
        sub.price.product.metadata.codename === 'unlimited_indoor_parking' &&
        sub.valid === true,
    );

    return subs;
  }

  get activeIndoorSubscription() {
    if (!this.canManageIndoor) {
      return false;
    }

    const activeSub = this.subscriptions.find(
      (sub: Subscription) =>
        sub.valid === true &&
        sub.canceledAt === null &&
        sub.price.product.metadata.codename === 'unlimited_indoor_parking',
    );

    return activeSub || false;
  }

  get inactiveIndoorSubscription() {
    if (!this.canManageIndoor) {
      return false;
    }

    const inactiveSub = this.subscriptions.find(
      (sub: Subscription) =>
        sub.valid === true &&
        sub.canceledAt !== null &&
        sub.price.product.metadata.codename === 'unlimited_indoor_parking',
    );

    return inactiveSub || false;
  }

  get canManageOutdoor() {
    if (!this.subscriptions.length) {
      return false;
    }

    const subs = this.subscriptions.some(
      (sub: Subscription) =>
        sub.price.product.metadata.codename === 'unlimited_outdoor_parking' &&
        sub.valid === true,
    );

    return subs;
  }

  get activeOutdoorSubscription() {
    if (!this.canManageOutdoor) {
      return false;
    }

    const activeSub = this.subscriptions.find(
      (sub: Subscription) =>
        sub.valid === true &&
        sub.canceledAt === null &&
        sub.price.product.metadata.codename === 'unlimited_outdoor_parking',
    );

    return activeSub || false;
  }

  get inactiveOutdoorSubscription() {
    if (!this.canManageOutdoor) {
      return false;
    }

    const inactiveSub = this.subscriptions.find(
      (sub: Subscription) =>
        sub.valid === true &&
        sub.canceledAt !== null &&
        sub.price.product.metadata.codename === 'unlimited_outdoor_parking',
    );

    return inactiveSub || false;
  }

  formatDate(date: string) {
    if (!date) {
      return;
    }

    return moment(date).format('D MMMM, YYYY');
  }

  async manageSubscription() {
    if (!this.user) {
      return;
    }

    const session = new BillingSession({
      user: this.user,
    });

    await session.save({ with: ['user.id'] });
    if (session.url) {
      window.location.href = session.url;
    }
  }

  async newSubscription(type: string) {
    if (!this.user) {
      return;
    }

    let price;
    switch (type) {
      case 'indoor':
        if (!this.indoorPrice) {
          return;
        }
        price = this.indoorPrice;
        break;

      case 'outdoor':
        if (!this.outdoorPrice) {
          return;
        }
        price = this.outdoorPrice;
        break;

      default:
        return;
    }

    const session = new CheckoutSession({
      price,
      user: this.user,
    });
    try {
      await session.save({ with: ['user.id', 'price.id'] });
      if (session.sessionId) {
        this.stripe.redirectToCheckout({
          sessionId: session.sessionId,
        });
      }
    } catch (err) {
      // Error
      err.response.errors.forEach((e: ApiError) =>
        snackModule.showSnackbar(e.detail),
      );
    }
  }

  async mounted() {
    if (!this.user) {
      return;
    }

    try {
      this.subscriptions = (
        await Subscription.includes('price.product')
          .where({ user: this.user.id })
          .all()
      ).data;
    } catch (err) {
      this.errorMessage = err.response.errors[0].detail;
    }

    try {
      this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_API_KEY);
    } catch (err) {
      // Error
      this.errorMessage = err.response.errors[0].detail;
    }
    try {
      this.products = (await Product.includes(['prices']).all()).data;
    } catch (err) {
      // Error
      err.response.errors.forEach((e: ApiError) =>
        snackModule.showSnackbar(e.detail),
      );
    }
  }
}
